/**
 * Preview tile for a queued file.
 *
 * Encapsulates the three things every preview needs and each call site used to get
 * subtly wrong: a downscaled thumbnail rather than the full-resolution original, a
 * placeholder for the window before it exists (an `<img>` with an empty `src` draws
 * a broken-image icon), and browser hints so decoding stays off the critical path.
 */

import { useEffect, useState } from "react";
import { FileText } from "@phosphor-icons/react";

import { createThumbnailBlob } from "shared/utils/imageThumbnail";

interface FileThumbnailProps {
  file: File;
  /** Sizing/shape comes from the caller; this component owns only the content. */
  className?: string;
  /** Longest edge of the generated thumbnail. */
  maxDimension?: number;
}

/**
 * Generates (or reuses) a thumbnail for one file.
 *
 * The blob URL is created in an effect rather than a memo, for the same reason as
 * `useObjectUrl`: under StrictMode a memo would not re-run after the cleanup
 * revoked the URL, leaving a dead `src` behind.
 */
const useFileThumbnail = (file: File, maxDimension?: number): string => {
  const [url, setUrl] = useState("");

  useEffect(() => {
    let cancelled = false;
    let createdUrl = "";

    createThumbnailBlob(file, maxDimension).then((blob) => {
      if (cancelled || !blob) return;
      createdUrl = URL.createObjectURL(blob);
      setUrl(createdUrl);
    });

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
      setUrl("");
    };
  }, [file, maxDimension]);

  return url;
};

export const FileThumbnail = ({
  file,
  className = "",
  maxDimension,
}: FileThumbnailProps) => {
  const thumbnailUrl = useFileThumbnail(file, maxDimension);
  const isImage = file.type.startsWith("image/");

  if (!isImage) {
    return (
      <div
        className={`bg-neutral-secondary-light flex items-center justify-center ${className}`}
      >
        <FileText size={24} className="text-muted-foreground" aria-hidden />
      </div>
    );
  }

  if (!thumbnailUrl) {
    // Neutral block while the thumbnail is being generated.
    return (
      <div
        className={`bg-neutral-secondary-light ${className}`}
        aria-hidden
      />
    );
  }

  return (
    <img
      src={thumbnailUrl}
      alt={file.name}
      className={className}
      // Off-screen cards in a long queue should not compete for decode time.
      loading="lazy"
      decoding="async"
    />
  );
};

export default FileThumbnail;
